import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { en, fr } from './i18n';
import { useTranslation, Trans } from 'react-i18next';
import { IProduct, IProductEdp } from './IProduct';

export interface PdpProps {
    SelectedEdpItem: IProductEdp;
    ProductData: IProduct;
    Selector?: any;
}
const domNode = document.getElementById('pricesPortal');
domNode.innerHTML = '';
const PdpPrice = ({ SelectedEdpItem, ProductData, Selector }: PdpProps) => {
    const { i18n, t } = useTranslation('pdp-price');

    const {
        Name,
        ItemNo,
        DefaultEdp,
        IsSoldOut,
        IsPriceRange,
        WasPriceRange,
        ProductReviewCount,
        ProductReviewRating,
        Styles = [],
        Sizes = [],
        AvailableSwatches,
        Edps,
        Brand,
        BrandDimensionId,
        BadgeImagePath,
        DeliveryOptions,
        ProductTypePath,
        ShowFreeShipEligibleMessage,
    } = ProductData;

    i18n.addResourceBundle('en', 'pdp-price', en, true, true);
    i18n.addResourceBundle('fr', 'pdp-price', fr, true, true);

    const isPrice = useMemo(() => {
        const currentEdp = SelectedEdpItem;
        if (!currentEdp || !currentEdp?.AppliedPrice) {
            return IsPriceRange;
        }
        return currentEdp.AppliedPrice;
    }, [IsPriceRange, SelectedEdpItem]);

    const wasPrice = useMemo(() => {
        const currentEdp = SelectedEdpItem;
        if (!currentEdp || !currentEdp?.WasPrice) {
            return WasPriceRange;
        }
        return currentEdp.WasPrice;
    }, [WasPriceRange, SelectedEdpItem]);

    const showWasPrice = useMemo(() => {
        return wasPrice !== isPrice;
    }, [wasPrice, isPrice]);

    const saving = useMemo(() => {
        const currentEdp = SelectedEdpItem;
        if (!currentEdp) {
            return '';
        }
        return currentEdp.SavePrice;
    }, [SelectedEdpItem]);

    const shipping = useMemo(() => {
        let edp = SelectedEdpItem;
        if (!edp) {
            edp = SelectedEdpItem;
        }
        const text = edp.AppliedShipping;
        if (text === '') {
            return {
                show: true,
                text: t('freeShipping'),
                isFreeShipping: true,
            };
        }
        return {
            show: !(ShowFreeShipEligibleMessage && edp.Weight < 20),
            text,
            isFreeShipping: false,
        };
    }, [SelectedEdpItem, t]);

    const priceDisplay = () => {
        return (
            <>
                <div className="pdp-description__prices--layout">
                    <div className="pdp-description__prices--is-price">{isPrice}</div>
                    {showWasPrice && (
                        <div className="pdp-description__prices--was-price">
                            <span className="visually-hidden">{t('wasText')}</span>
                            {wasPrice}
                        </div>
                    )}
                </div>

                {Selector && (
                    <div className="pdp-description__prices__saving-and-shipping">
                        {shipping.show && (
                            <div className="pdp-description__prices__saving-and-shipping__shipping">
                                {shipping.isFreeShipping && (
                                    <span className="pdp-description__prices__saving-and-shipping__shipping--free-shipping">
                                        {shipping.text}
                                    </span>
                                )}
                                {!shipping.isFreeShipping && (
                                    <span>
                                        {t('shippingText')}:{' '}
                                        <span className="pdp-description__prices__saving-and-shipping__shipping-amount">
                                            {shipping.text}
                                        </span>
                                    </span>
                                )}
                            </div>
                        )}
                        {saving && (
                            <div className="pdp-description__prices__saving-and-shipping__savings">
                                {t('savingText')}:{' '}
                                <span className="pdp-description__prices__saving-and-shipping__savings-amount">
                                    {saving}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </>
        );
    };

    if (!Selector) {
        return priceDisplay();
    }

    return ReactDOM.createPortal(priceDisplay(), domNode);
};

export default PdpPrice;
